import type { Infer } from 'spacetimedb';
import {
  ChatChannel,
  MyChatCommandsRow,
  MyFriendsRow,
  MyGuildRow,
  MyMailboxMessagesRow,
  MyIncomingTradesRow,
  MyOutgoingTradesRow,
  MyUnreadMailboxCountsRow,
  MyTradeTargetInventoryRow,
  MyRelatedTradeInventoriesRow,
  reducers
} from '../generated';
import type { ConnectionManager } from '../core/ConnectionManager';
import type { ReducerClient } from '../core/ReducerClient';
import type { SubscriptionFactory } from '../core/SubscriptionFactory';
import type { Logger } from '../types';

type MyFriendsTableRow = Infer<typeof MyFriendsRow>;
type MyGuildTableRow = Infer<typeof MyGuildRow>;
type MyChatCommandsTableRow = Infer<typeof MyChatCommandsRow>;
type MyMailboxMessagesTableRow = Infer<typeof MyMailboxMessagesRow>;
type MyIncomingTradesTableRow = Infer<typeof MyIncomingTradesRow>;
type MyOutgoingTradesTableRow = Infer<typeof MyOutgoingTradesRow>;
type MyUnreadMailboxCountsTableRow = Infer<typeof MyUnreadMailboxCountsRow>;
type MyTradeTargetInventoryTableRow = Infer<typeof MyTradeTargetInventoryRow>;
type MyRelatedTradeInventoriesTableRow = Infer<typeof MyRelatedTradeInventoriesRow>;
type ChatChannelValue = Infer<typeof ChatChannel>;

export interface ChatMessage {
  messageId: number;
  senderId: string;
  senderName: string;
  channel: unknown;
  text: string;
  timestamp: unknown;
}

export interface MailboxRow {
  mailId: number;
  fromPlayerId: string;
  fromDisplayName: string;
  toPlayerId: string;
  toDisplayName: string;
  msg: string;
  trade: string;
  read: boolean;
  timeSent: unknown;
}

export interface TradeInventoryRow {
  ownerPlayerId: string;
  itemId: string;
  quantity: number;
  tradeable: boolean;
  metadataJson: string;
}

export interface ServerChatCommand {
  name: string;
  usage: string;
  description: string;
}

export interface MailboxUnreadCounts {
  unreadMessages: number;
  unreadIncomingTrades: number;
  hasAnyUnread: boolean;
}

export class SocialModule {
  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly reducerClient: ReducerClient,
    private readonly subscriptionFactory: SubscriptionFactory,
    private readonly logger: Logger = () => undefined
  ) { }

  // --- Chat ---

  public async sendChat(text: string, channel: ChatChannelValue): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'send_chat',
      reducers.sendChat.paramsType,
      { text, channel }
    );
  }

  public async subscribeToChat(onUpdate: (messages: ChatMessage[]) => void): Promise<{ unsubscribe: () => void }> {
    return this.subscriptionFactory.createArraySubscription(
      {
        sql: 'SELECT * FROM my_chat_feed',
        getTable: (db) => db.myChatFeed,
        mapRow: (row) => ({
          messageId: Number(row.messageId ?? 0),
          senderId: row.senderId,
          senderName: row.senderName,
          channel: row.channel,
          text: row.text,
          timestamp: row.timestamp,
        })
      },
      (rows) => {

        onUpdate(rows);
      }
    );
  }

  public async subscribeToChatCommands(
    onUpdate: (commands: ServerChatCommand[]) => void
  ): Promise<{ unsubscribe: () => void }> {
    return this.subscriptionFactory.createArraySubscription(
      {
        sql: 'SELECT * FROM my_chat_commands',
        getTable: (db) => db.myChatCommands,
        mapRow: (row: MyChatCommandsTableRow) => ({
          name: row.name,
          usage: row.usage,
          description: row.description,
        })
      },
      onUpdate
    );
  }

  // --- Friends ---

  public async addFriend(name: string): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'add_friend',
      reducers.addFriend.paramsType,
      { name }
    );
  }

  public async removeFriend(name: string): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'remove_friend',
      reducers.removeFriend.paramsType,
      { name }
    );
  }

  public async subscribeToFriendRoster(onUpdate: (rows: MyFriendsTableRow[]) => void): Promise<{ unsubscribe: () => void }> {
    return this.subscriptionFactory.createArraySubscription(
      {
        sql: 'SELECT * FROM my_friends',
        getTable: (db) => db.myFriends,
        mapRow: (row) => ({
          playerId: row.playerId,
          displayName: row.displayName,
          isOnline: Boolean(row.isOnline)
        })
      },
      onUpdate
    );
  }

  // --- Guild ---

  public async joinGuild(guildName: string): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'join_guild',
      reducers.joinGuild.paramsType,
      { guildName }
    );
  }

  public async createGuild(guildName: string): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'create_guild',
      reducers.createGuild.paramsType,
      { guildName }
    );
  }

  public async leaveGuild(): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'leave_guild',
      reducers.leaveGuild.paramsType,
      {}
    );
  }

  public async subscribeToGuild(onUpdate: (guild: MyGuildTableRow | null) => void): Promise<{ unsubscribe: () => void }> {
    return this.subscriptionFactory.createSingleRowSubscription(
      {
        sql: 'SELECT * FROM my_guild',
        getTable: (db) => db.myGuild,
        mapRow: (row) => ({
          guildName: row.guildName,
          members: row.members
        })
      },
      onUpdate
    );
  }

  public async subscribeToGuildRoster(onUpdate: (row: MyGuildTableRow | null) => void): Promise<{ unsubscribe: () => void }> {
    return this.subscriptionFactory.createSingleRowSubscription(
      {
        sql: 'SELECT * FROM my_guild',
        getTable: (db) => db.myGuild,
        mapRow: (row) => row
      },
      onUpdate
    );
  }

  // --- Mailbox ---

  public async deleteMailboxItem(mailId: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'delete_mail',
      paramsType: reducers.deleteMail.paramsType,
      args: { mailId: toUInt64Arg(mailId) },
      matchArgs: (args) => Number(args.mailId ?? 0) === mailId,
      onEvent: 'onDeleteMail',
      offEvent: 'removeOnDeleteMail',
      timeoutMessage: 'Delete mail timed out',
      failureMessage: 'Failed to delete mail'
    });
  }

  public async acceptMailboxTrade(mailId: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'accept_mail_trade',
      paramsType: reducers.acceptMailTrade.paramsType,
      args: { mailId: toUInt64Arg(mailId) },
      matchArgs: (args) => Number(args.mailId ?? 0) === mailId,
      onEvent: 'onAcceptMailTrade',
      offEvent: 'removeOnAcceptMailTrade',
      timeoutMessage: 'Accept trade timed out',
      failureMessage: 'Failed to accept trade'
    });
  }

  public async cancelOutgoingTrade(mailId: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'cancel_trade',
      paramsType: reducers.cancelTrade.paramsType,
      args: { mailId: toUInt64Arg(mailId) },
      matchArgs: (args) => Number(args.mailId ?? 0) === mailId,
      onEvent: 'onCancelTrade',
      offEvent: 'removeOnCancelTrade',
      timeoutMessage: 'Cancel trade timed out',
      failureMessage: 'Failed to cancel trade'
    });
  }

  public async markMessagesRead(): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'mark_messages_read',
      reducers.markMessagesRead.paramsType,
      {}
    );
  }

  public async markIncomingTradesRead(): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'mark_incoming_trades_read',
      reducers.markIncomingTradesRead.paramsType,
      {}
    );
  }

  public async markMailRead(mailId: number): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'mark_mail_read',
      reducers.markMailRead.paramsType,
      { mailId: toUInt64Arg(mailId) }
    );
  }

  public async sendMail(toName: string, msg: string, trade: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'send_mail',
      paramsType: reducers.sendMail.paramsType,
      args: { toName, msg, trade },
      onEvent: 'onSendMail',
      offEvent: 'removeOnSendMail',
      timeoutMessage: 'Send mail timed out',
      failureMessage: 'Failed to send mail'
    });
  }

  public async verifyTradeTarget(targetName: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'verify_trade_target',
      paramsType: reducers.verifyTradeTarget.paramsType,
      args: { targetName },
      onEvent: 'onVerifyTradeTarget',
      offEvent: 'removeOnVerifyTradeTarget',
      timeoutMessage: 'Trade target verification timed out',
      failureMessage: 'Trade target verification failed'
    });
  }

  public async verifyTradeTargetAndFetchInventory(targetName: string): Promise<TradeInventoryRow[]> {
    await this.verifyTradeTarget(targetName);
    return this.fetchTradeTargetInventory();
  }

  public async subscribeToMailboxMessages(onUpdate: (rows: MailboxRow[]) => void): Promise<{ unsubscribe: () => void }> {
    return this.subscriptionFactory.createArraySubscription(
      {
        sql: 'SELECT * FROM my_mailbox_messages',
        getTable: (db) => db.myMailboxMessages,
        mapRow: (row: MyMailboxMessagesTableRow): MailboxRow => ({
          mailId: Number(row.mailId ?? 0),
          fromPlayerId: row.fromPlayerId,
          fromDisplayName: row.fromDisplayName,
          toPlayerId: row.toPlayerId,
          toDisplayName: row.toDisplayName,
          msg: row.msg,
          trade: row.trade,
          read: Boolean(row.read),
          timeSent: row.timeSent
        })
      },
      onUpdate
    );
  }

  public async subscribeToIncomingTrades(onUpdate: (rows: MailboxRow[]) => void): Promise<{ unsubscribe: () => void }> {
    return this.subscriptionFactory.createArraySubscription(
      {
        sql: 'SELECT * FROM my_incoming_trades',
        getTable: (db) => db.myIncomingTrades,
        mapRow: (row: MyIncomingTradesTableRow): MailboxRow => ({
          mailId: Number(row.mailId ?? 0),
          fromPlayerId: row.fromPlayerId,
          fromDisplayName: row.fromDisplayName,
          toPlayerId: row.toPlayerId,
          toDisplayName: row.toDisplayName,
          msg: row.msg,
          trade: row.trade,
          read: Boolean(row.read),
          timeSent: row.timeSent
        })
      },
      onUpdate
    );
  }

  public async subscribeToOutgoingTrades(onUpdate: (rows: MailboxRow[]) => void): Promise<{ unsubscribe: () => void }> {
    return this.subscriptionFactory.createArraySubscription(
      {
        sql: 'SELECT * FROM my_outgoing_trades',
        getTable: (db) => db.myOutgoingTrades,
        mapRow: (row: MyOutgoingTradesTableRow): MailboxRow => ({
          mailId: Number(row.mailId ?? 0),
          fromPlayerId: row.fromPlayerId,
          fromDisplayName: row.fromDisplayName,
          toPlayerId: row.toPlayerId,
          toDisplayName: row.toDisplayName,
          msg: row.msg,
          trade: row.trade,
          read: Boolean(row.read),
          timeSent: row.timeSent
        })
      },
      onUpdate
    );
  }

  public async subscribeToTradeTargetInventory(
    onUpdate: (rows: TradeInventoryRow[]) => void
  ): Promise<{ unsubscribe: () => void }> {
    return this.subscriptionFactory.createArraySubscription(
      {
        sql: 'SELECT * FROM my_trade_target_inventory',
        getTable: (db) => db.myTradeTargetInventory,
        mapRow: (row: MyTradeTargetInventoryTableRow): TradeInventoryRow => ({
          ownerPlayerId: row.ownerPlayerId,
          itemId: row.itemId,
          quantity: Number(row.quantity ?? 0),
          tradeable: Boolean((row as MyTradeTargetInventoryTableRow & { tradeable?: boolean | null }).tradeable ?? true),
          metadataJson: row.metadataJson
        })
      },
      onUpdate
    );
  }

  public async subscribeToMailboxUnreadCounts(
    onUpdate: (counts: MailboxUnreadCounts | null) => void
  ): Promise<{ unsubscribe: () => void }> {
    return this.subscriptionFactory.createSingleRowSubscription(
      {
        sql: 'SELECT * FROM my_unread_mailbox_counts',
        getTable: (db) => db.myUnreadMailboxCounts,
        mapRow: (row: MyUnreadMailboxCountsTableRow): MailboxUnreadCounts => ({
          unreadMessages: Number(row.unreadMessages ?? 0),
          unreadIncomingTrades: Number(row.unreadIncomingTrades ?? 0),
          hasAnyUnread: Boolean(row.hasAnyUnread)
        })
      },
      onUpdate
    );
  }

  public async fetchTradeTargetInventory(): Promise<TradeInventoryRow[]> {
    return this.subscriptionFactory.runOneTimeQuery(
      {
        sql: 'SELECT * FROM my_trade_target_inventory',
        getTable: (db) => db.myTradeTargetInventory,
        mapRow: (row: MyTradeTargetInventoryTableRow): TradeInventoryRow => ({
          ownerPlayerId: row.ownerPlayerId,
          itemId: row.itemId,
          quantity: Number(row.quantity ?? 0),
          tradeable: Boolean((row as MyTradeTargetInventoryTableRow & { tradeable?: boolean | null }).tradeable ?? true),
          metadataJson: row.metadataJson
        })
      }
    );
  }

  public async fetchInventoryByPlayerId(playerId: string): Promise<TradeInventoryRow[]> {
    const rows = await this.subscriptionFactory.runOneTimeQuery(
      {
        sql: 'SELECT * FROM my_related_trade_inventories',
        getTable: (db) => db.myRelatedTradeInventories,
        mapRow: (row: MyRelatedTradeInventoriesTableRow): TradeInventoryRow => ({
          ownerPlayerId: row.ownerPlayerId,
          itemId: row.itemId,
          quantity: Number(row.quantity ?? 0),
          tradeable: Boolean((row as MyRelatedTradeInventoriesTableRow & { tradeable?: boolean | null }).tradeable ?? true),
          metadataJson: row.metadataJson
        })
      }
    );
    return rows.filter((row) => row.ownerPlayerId === playerId);
  }

  /**
   * @deprecated Legacy compatibility wrapper.
   * Prefer explicit subscriptions (`subscribeToFriendRoster`, `subscribeToGuild`, etc.).
   * This method intentionally keeps only lightweight view subscriptions alive so older
   * callers that expect a single social subscription handle do not break.
   */
  public async subscribeToSocial(): Promise<{ unsubscribe: () => void }> {
    const friendSub = await this.subscribeToFriendRoster(() => undefined);
    const guildSub = await this.subscribeToGuild(() => undefined);

    return {
      unsubscribe: () => {
        friendSub.unsubscribe();
        guildSub.unsubscribe();
      }
    };
  }
}

function toUInt64Arg(value: number): string {
  return String(Math.max(0, Math.floor(value)));
}
